/**
 * CategoryOverviewPage Component
 * Displays grid of all product categories
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useCategories } from '../../hooks/useProducts.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';

const CategoryOverviewPage = () => {
  const { data: categories = [], isLoading, error } = useCategories();

  if (isLoading) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="container-xxl py-5">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-3 text-muted">Loading categories...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="container-xxl py-5">
          <div className="text-center">
            <div className="alert alert-danger">
              <h4>Error Loading Categories</h4>
              <p>{error.message || 'Failed to load categories'}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      {/* Header Section */}
      <section className="bg-white py-4 border-bottom">
        <Container fluid className="container-xxl">
          <Row>
            <Col>
               <div className="d-flex align-items-center justify-content-between flex-column flex-md-row">
                <div className="mb-4 mb-md-0 text-center text-md-start">
                  <h1 className="h2 mb-2">Product Categories</h1>
                  <p className="text-muted mb-0">
                    Browse our {categories.length} product categories
                  </p>
                </div>
                <div>
                  <Link to="/" className="btn btn-outline-primary">
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Home
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Categories Grid */}
      <section className="py-4">
        <Container fluid className="container-xxl">
          {categories.length > 0 ? (
            <Row>
              {categories.map((category) => (
                <Col key={category.id} xl={3} lg={4} md={6} className="mb-4">
                  <Card className="h-100 border-0 shadow-sm category-card">
                   
                    <Card.Body className="text-center">
                      <Card.Title className="category-card__title">
                        {category.name}
                      </Card.Title>
                      
                      {category.productCount && (
                        <Card.Text className="category-card__count">
                          {category.productCount} products available
                        </Card.Text>
                      )}
                      
                      <Link 
                        to={`/categories/${category.slug}`}
                        className="btn btn-primary category-card__button"
                      >
                        Browse {category.name}
                        <i className="bi bi-arrow-right ms-2"></i>
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-grid display-1 text-muted mb-3"></i>
              <h3 className="text-muted">No Categories Available</h3>
              <p className="text-muted">Please contact support if you believe this is an error.</p>
              <Link to="/" className="btn btn-primary">
                Return to Home
              </Link>
            </div>
          )}
        </Container>
      </section>

      <style>{`
        .category-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .category-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }

        .category-card__image-wrapper {
          padding: 30px;
          background: linear-gradient(135deg, #f8f9fa, #e9ecef);
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 120px;
        }

        .category-card__icon {
          max-width: 60px;
          max-height: 60px;
          object-fit: contain;
        }

        .category-card__icon-placeholder {
          color: #6c757d;
          font-size: 3rem;
        }

        .category-card__title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: #333;
        }

        .category-card__count {
          color: #6c757d;
          margin-bottom: 20px;
          font-size: 0.9rem;
        }

        .category-card__button {
          border-radius: 25px;
          padding: 8px 20px;
          font-weight: 500;
        }

        .category-card__button:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default CategoryOverviewPage;